import { type HTMLAttributes } from "react"
import {
  Input as AriaInput,
  type TextFieldProps as AriaTextFieldProps,
  type TextFieldRenderProps as AriaTextFieldRenderProps,
  TextField as AriaTextField,
  Label as AriaLabel,
  Text as AriaText,
  FieldError as AriaFieldError,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { LabelAndDescriptionStack } from "./Field"

export type TextFieldProps = AriaTextFieldProps & {
  description?: string
  errorMessage?: string
  label?: string
  placeholder?: string
  /** An escape hatch for any additional props you want to pass to the input. */
  inputProps?: HTMLAttributes<HTMLInputElement>
  size?: "small" | "medium" | "large"
  slots?: {
    leading?: AriaTextFieldProps["children"]
    trailing?: AriaTextFieldProps["children"]
  }
}

type SlotProps = HTMLAttributes<HTMLDivElement>

export const Slot = ({ children, className, ...props }: SlotProps) => (
  <div className={twMerge("tt-slot", className)} {...props}>
    {children}
  </div>
)

const makeSlot = (
  slot: AriaTextFieldProps["children"],
  state: AriaTextFieldRenderProps & {
    defaultChildren: React.ReactNode | undefined
  }
) => {
  if (typeof slot === "function") {
    return slot(state)
  } else if (slot) {
    return <Slot>{slot}</Slot>
  }

  return null
}

/**
 * A form field that allows users to input text.
 *
 * ```tsx
 * import { TextField } from "@tokenterminal/ui/input/TextField"
 *
 * <TextField
 *   label="Email"
 *   placeholder="Enter your email"
 *   description="We'll never share your email with anyone else."
 * />
 * ```
 */

export const TextField = ({
  description,
  errorMessage,
  inputProps,
  label,
  placeholder,
  size = "medium",
  slots = {},
  ...props
}: TextFieldProps) => {
  return (
    <AriaTextField className="tt-field-wrapper" {...props}>
      {(state) => (
        <>
          {(label || description) && (
            <LabelAndDescriptionStack>
              {label && (
                <AriaLabel className="tt-field-label">{label}</AriaLabel>
              )}
              {description && (
                <AriaText slot="description" className="tt-field-description">
                  {description}
                </AriaText>
              )}
            </LabelAndDescriptionStack>
          )}
          <div data-size={size} className="tt-input-container">
            {makeSlot(slots.leading, state)}
            <AriaInput
              className="tt-input-base"
              placeholder={placeholder}
              {...inputProps}
            />
            {makeSlot(slots.trailing, state)}
          </div>
          <AriaFieldError className="tt-field-error">
            {errorMessage}
          </AriaFieldError>
        </>
      )}
    </AriaTextField>
  )
}
